import React, { memo, useCallback, useRef } from 'react'
import { intl } from 'lang/intl'
import {
  TtTrayItem,
  TtTrayChyronOnCamera,
  TtIconButton,
  TtIcon,
} from '@23/react'
import { useSortable } from '@dnd-kit/sortable'
import {
  useOverlayState,
  setChyron,
} from '../../../../firebase/hooks/useOverlays'
import Toolbar from '../Toolbar'
import GutterMenu from '../GutterMenu'
import useTrayTracking from '../useTrayTracking'
import { useScrollRegister, useScroll } from '../ScrollProvider'
import { TrayCardChyronOnCamera } from '../../../../firebase/model/tray-cards/types/chyron-on-camera-model'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import { setActiveCard, removeCard } from '../../../../firebase/hooks/useTray'

import './ChyronOnCameraCard.scss'

interface OnCameraCardProps {
  isActiveCard: boolean
  isPriorToSelectedCard: boolean
  isLastCard: boolean
  position: number
  card: TrayCardChyronOnCamera
}

const OnCameraCard = ({
  isActiveCard,
  isPriorToSelectedCard,
  isLastCard,
  position,
  card,
}: OnCameraCardProps): JSX.Element => {
  const trayTracking = useTrayTracking()
  const ref = useRef<HTMLTtTrayItemElement>(null)
  useScrollRegister(card.id, position)
  const { scrollIntoView } = useScroll()

  const { setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: card.id,
    })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const overlayState = useOverlayState()
  const isChyronCurrentlyShowing =
    !!overlayState && overlayState.type === ChyronType.J2SIntroduction

  const onCardClickHandler = () => {
    trayTracking.activated(card.id)
    setActiveCard(card.id)
    scrollIntoView(card.id)
    setChyron({
      type: ChyronType.J2SIntroduction,
    })
  }

  const onRemoveChyronClickHandler = () => {
    setChyron(null)
  }

  const onDeleteCard = () => {
    if (isChyronCurrentlyShowing) {
      setChyron(null)
    }
    trayTracking.deleted(card.id)
    removeCard(card.id)
  }

  const getGutterMode = useCallback(() => {
    if (isSorting) return 'disabled'
    if (isLastCard) return 'persisted'
    return 'normal'
  }, [isLastCard, isSorting])

  setNodeRef(ref.current)

  return (
    <TtTrayItem
      data-id={card.id}
      ref={ref}
      style={style}
      gutterDelay={400}
      gutterMode={getGutterMode()}
    >
      <TtTrayChyronOnCamera
        tag={intl.formatMessage({ defaultMessage: 'Go on Camera' })}
        selected={isActiveCard}
        disabled={isPriorToSelectedCard}
        onTt-click={(event) => {
          if (event.target === event.currentTarget) {
            onCardClickHandler()
          }
        }}
      >
        {isChyronCurrentlyShowing ? (
          <TtIconButton
            slot="toolbar"
            library="one/webinars"
            name="visibility-off"
            label="Visibility off"
            onClick={onRemoveChyronClickHandler}
            className="visibility-icon"
          />
        ) : (
          <TtIconButton
            slot="toolbar"
            library="one/webinars"
            name="visibility-on"
            label="Visibility on"
            onClick={onCardClickHandler}
            className="visibility-icon"
          />
        )}
        <Toolbar cardId={card.id} onDeleteClick={onDeleteCard} />
        <div className="go-on-camera-info">
          <div className="suggestion-text">
            {intl.formatMessage(
              {
                defaultMessage:
                  'You can now request access to go on camera.{linebreak}Just press the {icon} button on top of the sidebar.',
              },
              {
                icon: (
                  <span className="suggestion-text-info">
                    <TtIcon className="mic-icon" name="record" library="23" />
                    {intl.formatMessage({
                      defaultMessage: 'Go on camera',
                    })}
                  </span>
                ),
                linebreak: <br />,
              }
            )}
          </div>
        </div>
      </TtTrayChyronOnCamera>
      <GutterMenu cardId={card.id} />
    </TtTrayItem>
  )
}

export default memo(OnCameraCard)
